import { inject, Injectable } from '@angular/core';
import type {
  OrganizationInvitationResource,
  OrganizationMembershipRequestResource,
  OrganizationResource,
} from '@clerk/types';
import { MemberDto } from '@nusign/picflow-backend-types';
import { catchError, firstValueFrom, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { standardMemoryCacheExpiry } from '../consts';
import { PICFLOW_CONFIG } from '../picflow-config';
import { Invitation, JoinRequest } from '../types/invitation.type';
import { DataLoaderService } from './data-loader.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private state = inject(StateService);
  private config = inject(PICFLOW_CONFIG);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);
  private toasts = inject(ToastService);
  private auth = inject(AuthService);

  public createOrganization(name: string, path: string) {
    return this.auth.clerk.createOrganization({ name, slug: path });
  }

  public async updateOrganization(params: { name: string; slug?: string }) {
    const organization = await this.getOrganization();
    if (!organization) return console.warn('Organization not found');
    try {
      await organization.update(params);
    } catch (err) {
      const error = err.errors[0];
      if (error.code) {
        this.toasts.add(error.longMessage, 'error');
        return undefined;
      }
      throw err;
    }
    const activeTenant = this.state.activeTenant$?.value;
    this.loader.remove('organization_' + activeTenant.clerk_organization_id);
    return true;
  }

  public inviteUser(email: string, role: 'admin' | 'member') {
    // this.observableLoader.remove('invites');
    return from(this.getOrganization()).pipe(
      switchMap(org =>
        from(org.inviteMember({ emailAddress: email, role: role === 'admin' ? 'admin' : 'org:member' }))
      ),
      tap(invite => {
        this.observableLoader.update('invites', (invites: Invitation[]) => {
          return [...(invites || []), this.mapInvite(invite)];
        });
      })
    );
  }

  private getOrgInvites(org: OrganizationResource) {
    return from(org.getInvitations({ pageSize: 500, status: ['pending'] })).pipe(
      catchError(() => of({ data: [] as OrganizationInvitationResource[] }))
    );
  }

  public getPendingInvites(): Observable<Invitation[]> {
    return this.observableLoader.get(
      'invites',
      () => {
        return from(this.getOrganization()).pipe(
          switchMap(org => this.getOrgInvites(org as OrganizationResource)),
          map(resp => {
            const invites = resp.data.filter(i => i.status === 'pending');
            return invites.map(invite => this.mapInvite(invite));
          })
        );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  private mapInvite(invite: OrganizationInvitationResource): Invitation {
    const activeTenant = this.state.activeTenant$.value;
    return {
      id: invite.id,
      created_at: invite.createdAt.getTime() / 1000,
      updated_at: invite.updatedAt.getTime() / 1000,
      tenant: activeTenant.id,
      email: invite.emailAddress,
      role: invite.role === 'admin' ? 'admin' : 'member',
    };
  }

  private getOrgRequests(org: OrganizationResource) {
    return from(org.getMembershipRequests({ pageSize: 500 })).pipe(
      catchError(() => of({ data: [] as OrganizationInvitationResource[] }))
    );
  }

  public getPendingRequests(): Observable<JoinRequest[]> {
    return this.observableLoader.get(
      'joinRequests',
      () => {
        return from(this.getOrganization()).pipe(
          switchMap(org => this.getOrgRequests(org as OrganizationResource)),
          map(resp => {
            const invites = resp.data.filter(i => i.status === 'pending');
            return invites.map(invite => this.mapRequest(invite));
          })
        );
      },
      standardMemoryCacheExpiry,
      false
    );
  }

  private mapRequest(request: OrganizationInvitationResource | OrganizationMembershipRequestResource): JoinRequest {
    if ('accept' in request) {
      const activeTenant = this.state.activeTenant$.value;
      return {
        id: request.id,
        created_at: request.createdAt.getTime() / 1000,
        updated_at: request.updatedAt.getTime() / 1000,
        tenant: activeTenant.id,
        email: request.publicUserData.identifier,
        accept: request.accept,
        reject: request.reject,
        first_name: request.publicUserData.firstName,
        last_name: request.publicUserData.lastName,
      };
    }
  }

  public deleteInvite(inviteId: string) {
    return from(this.getOrganization()).pipe(
      switchMap(org => this.getOrgInvites(org as OrganizationResource)),
      switchMap(resp => {
        const invites = resp.data.filter(i => i.status === 'pending');
        const invite = invites.find(i => i.id === inviteId);
        if (!invite) {
          console.warn('invite not found', invites, inviteId);
          return of();
        }
        return invite.revoke();
      }),
      tap(() =>
        this.observableLoader.update<Invitation[]>('invites', invites => invites?.filter(i => i.id !== inviteId) || [])
      )
    );
  }

  public getMembers() {
    return this.observableLoader.get(
      'members',
      () =>
        this.getAllService.getAll<MemberDto>(`${this.config.apiEndpoint}/members`).pipe(
          map(members => {
            members.forEach(m => {
              m['name'] = m.last_name ? `${m.first_name}${m.last_name}` : m.first_name;
            });
            const myId = this.state.user.id;
            return [
              members.find(m => m.id === myId),
              ...members.filter(m => m.role === 'owner' && m.id !== myId),
              ...members.filter(m => m.role === 'admin' && m.id !== myId),
              ...members.filter(m => m.role === 'member' && m.id !== myId),
            ].filter(Boolean);
          }),
          catchError(err => {
            console.error(err);
            return of([] as MemberDto[]);
          })
        ),
      standardMemoryCacheExpiry,
      false
    );
  }

  public async updateMember(memberId: string, params: { role: 'admin' | 'member' | 'owner' }) {
    const members = await firstValueFrom(this.getMembers());
    const member = members.find(m => m.id === memberId);

    if (!member) return;

    const organization = await this.getOrganization();
    const memberships = await organization.getMemberships({ pageSize: 500 });
    const membership = memberships.data.find(m => m.publicUserData.userId === member.sub);
    if (!membership) return;

    const role = 'org:' + params.role;
    membership.update({ role });

    this.observableLoader.update('members', (members: MemberDto[]) => {
      return members.map(m => {
        if (m.id === memberId) {
          return { ...m, role: params.role };
        }
        return m;
      });
    });
  }

  public async getOrganization(orgId = this.state.activeTenant$.value.clerk_organization_id) {
    return this.loader.get('organization_' + orgId, async () => {
      await this.auth.init();
      return await this.auth.clerk?.getOrganization(orgId);
    });
  }

  public async removeMember(memberId: string) {
    const [members, organization] = await Promise.all([firstValueFrom(this.getMembers()), this.getOrganization()]);
    const member = members.find(m => m.id === memberId);
    if (!member) return;
    const memberships = await organization.getMemberships({ pageSize: 500 });
    const membership = memberships.data.find(m => m.publicUserData.userId === member.sub);
    if (!membership) return;
    this.observableLoader.update('members', (members: MemberDto[]) => members?.filter(m => m.id !== memberId) || []);
    await membership.destroy();
  }
}
